import * as React from "react";
import SearchableSelect from "@/lib/shadcn/components/searchable-select";
import { AddressCreateDialog } from "@/lib/shadcn/components/address-create-dialog";
import { useGetAddressesQuery } from "@/store/booking-management-api";
import { matchesAddressSearch } from "@/utils/addressIdentity";

/**
 * The apartment forms' address control (address-normalization/FR-009, /FR-010).
 *
 * An address is PICKED from the registered buildings — it is not free text. The
 * search folds identity-insensitive differences on BOTH sides, which is what
 * makes the control usable rather than merely safe: cmdk's default filter scores
 * the raw label, so an operator typing a Latin `K` would match nothing against a
 * Greek `Καλλιρόης`, conclude the building is missing, and create the very
 * duplicate this feature exists to prevent.
 */
interface AddressSelectProps {
    /** The currently selected address id, or null when none is chosen yet. */
    value: number | null;
    onChange: (addressId: number) => void;
    disabled?: boolean;
    /** Placed on the trigger so an external `<label htmlFor>` can bind to it. */
    id?: string;
    /**
     * Marks the control required for assistive tech. The forms genuinely block
     * submission on it (FR-014), so leaving this off would let a screen-reader
     * operator fill everything else before discovering the field was mandatory.
     */
    required?: boolean;
    /** Id of an element explaining what the control is holding up, if anything. */
    "aria-describedby"?: string;
}

/** cmdk scoring: 1 when the folded query occurs in the folded label, else 0. */
function foldedFilter(optionLabel: string, search: string): number {
    return matchesAddressSearch(optionLabel, search) ? 1 : 0;
}

export const AddressSelect: React.FC<AddressSelectProps> = ({
    value,
    onChange,
    disabled = false,
    id,
    required = false,
    "aria-describedby": describedBy,
}) => {
    // The control owns its own query rather than taking the list as a prop —
    // the same shape as TagMultiSelect / HostHubRentalSelector, so a page that
    // renders it never has to know where addresses come from.
    const { data: addresses = [] } = useGetAddressesQuery();
    const options = React.useMemo(
        () => addresses.map((address) => ({ value: String(address.id), label: address.canonical })),
        [addresses],
    );

    // The inline create panel (address-normalization/FR-011). It is mounted only
    // while open so every visit starts from empty fields and no stale warning.
    const [creating, setCreating] = React.useState(false);

    // Closing the create panel unmounts the field the keyboard is in, and the
    // "＋ Add a new address" button that opened it went with the popover, so the
    // keyboard would fall back to `document.body`. The trigger is the control the
    // operator was working in and it is still here, so it takes focus back on
    // BOTH ways out of the panel (WCAG 2.4.3).
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const returnFocusToTrigger = React.useCallback(() => {
        triggerRef.current?.focus();
    }, []);

    return (
        <>
            <SearchableSelect
                triggerRef={triggerRef}
                variant="outline"
                width="w-full"
                data-testid="address-select"
                id={id}
                // An `aria-label` OVERRIDES an associated `<label htmlFor>`, so a
                // hardcoded one here would hide the pages' "Address*" — the required
                // marker the sighted operator sees — from assistive tech. It is kept
                // only as the fallback for a mount that supplies no id to label.
                aria-label={id === undefined ? "Address" : undefined}
                aria-required={required}
                aria-describedby={describedBy}
                placeholder="Select Address"
                options={options}
                selectedValue={value != null ? String(value) : ""}
                onChange={(selected) => onChange(Number(selected))}
                disabled={disabled}
                filter={foldedFilter}
                footer={(closePopover) => (
                    // Pinned below the list rather than offered as an option: the
                    // operator reaches for it exactly when their query matched
                    // nothing, and a cmdk item would have been filtered away.
                    <div className="border-t p-1">
                        <button
                            type="button"
                            data-testid="address-create-open"
                            className="w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                            onClick={() => {
                                closePopover();
                                setCreating(true);
                            }}
                        >
                            ＋ Add a new address
                        </button>
                    </div>
                )}
            />
            {creating && (
                <AddressCreateDialog
                    onCancel={() => {
                        setCreating(false);
                        returnFocusToTrigger();
                    }}
                    onAddressChosen={(address) => {
                        setCreating(false);
                        onChange(address.id);
                        returnFocusToTrigger();
                    }}
                />
            )}
        </>
    );
};

export default AddressSelect;
